import { baseLocale, cookieDomain, cookieMaxAge, cookieName } from '../localization/out/runtime.js';

/**
 * Pins the UI locale before anything renders.
 *
 * Every paraglide runtime in the bundle resolves with the strategy
 * `["cookie", "globalVariable", "baseLocale"]`. `preferredLanguage` is not in it, so
 * the browser's own language is never consulted, and `globalVariable` is module-local
 * — each compiled package carries its own copy, so they cannot agree through it. The
 * cookie is the only channel they share: all of them read `PARAGLIDE_LOCALE`.
 *
 * That matters because they disagree about the default. This app compiles
 * `baseLocale: "en"`, while `@adornis/entity-relationship-management`, `@adornis/user-forms`
 * and `@adornis/filter` each compile `baseLocale: "de"`. With no cookie set, whichever
 * runtime resolves first writes its own base locale into the shared cookie and every
 * other runtime reads it back — so the admin area, the login form and the list filters
 * came up German for every visitor regardless of browser language, and stayed that way
 * because the value persisted.
 *
 * The cookie is therefore written here instead of being left to whichever runtime gets
 * there first. It is rewritten on every start rather than only when absent, so a stale
 * `de` from before this fix heals itself.
 *
 * `baseLocale` is this app's own, and it is the only locale it actually ships messages
 * for — `src/localization/messages/` contains `en.json` and nothing else. When a second
 * translation lands, this is the place to decide: negotiate `navigator.languages` against
 * the runtime's `locales`, and let a language picker write over the result.
 */
export function initLocale(): void {
  // Guarded for the node-worker and server bundles, which import the client entry for
  // its side effects but have no document to write to.
  if (typeof document === 'undefined') return;

  // Written exactly the way paraglide writes it in `setLocale`, so this overwrites that
  // cookie rather than sitting beside it on a different path.
  const cookie = `${cookieName}=${baseLocale}; path=/; max-age=${cookieMaxAge}`;
  document.cookie = cookieDomain ? `${cookie}; domain=${cookieDomain}` : cookie;
}
